import { Injectable } from '@angular/core';
import { core, isNumber, isObject, logger } from '@icc/shared/helpers/b2b';
import { ReplaySubject, Subject } from 'rxjs';
import { ConfiguratorData } from './interfaces/configurator-data';

export interface LoadedConfiguratorsDataValue {
    [prop: string]: any;
}

@Injectable({
    providedIn: 'root'
})
export abstract class ConfiguratorsDataService<T extends keyof ConfiguratorData = keyof ConfiguratorData> {
    abstract get data(): ConfiguratorData[T];
    data$: Subject<ConfiguratorData[T]> = new ReplaySubject<ConfiguratorData[T]>();

    abstract loaded: any;

    abstract load(configuratorType: string, configuratorId: number, marketId: number, language: string, updateGlobal?: boolean, ): Promise<any>;

    get<Type extends any = any>(type: string, ...ids: (number | { val: any; key: string } | string)[]): Type | null {
        if (!this.loaded) {
            throw new Error('Nie załadowano danych');
        }
        if (ids.length === 0) {
            throw new Error('Nie podano wyszukiwanego id');
        }
        let keys = [];
        [type, ...keys] = type.split('.');
        const path = keys.join('.');
        if (Array.isArray(this.data[type])) {
            const conditions = [];
            if (isNumber(ids[0])) {
                conditions.push({ val: ids[0], key: 'id', type: 'number' });
            } else {
                conditions.push(ids[0]);
            }
            const foundedObjects = this.data[type].filter(obj =>
                conditions.every(
                    condition =>
                        condition != null
                        && ((condition.type === 'number'
                            && Number(core.deepFind(obj, condition.key)) === Number(condition.val))
                            || core.deepFind(obj, condition.key) === condition.val)
                )
            );
            if (foundedObjects.length > 0) {
                if (keys.length > 0) {
                    return core.copy(core.deepFind(foundedObjects[0], path));
                } else {
                    return core.copy(foundedObjects[0]);
                }
            } else {
                logger.warn(
                    'Brak danych w kluczu '
                        + type
                        + ' dla podanych parametrów '
                        + core.stringJson(ids)
                );
                return null;
            }
        } else if (isObject(this.data[type])) {
            const matchedElement = core.deepFind(this.data[type], ids[0]);
            if (Array.isArray(matchedElement)) {
                const conditions = [];
                if (isNumber(ids[1])) {
                    conditions.push({ val: ids[1], key: 'id', type: 'number' });
                } else {
                    conditions.push(ids[1]);
                }
                const foundedObjects = matchedElement.filter(obj =>
                    conditions.every(
                        condition =>
                            condition !== null
                            && ((condition.type === 'number'
                                && Number(core.deepFind(obj, condition.key))
                                    === Number(condition.val))
                                || core.deepFind(obj, condition.key) === condition.val)
                    )
                );
                if (foundedObjects.length > 0) {
                    if (keys.length > 0) {
                        return core.copy(core.deepFind(foundedObjects[0], path));
                    } else {
                        return core.copy(foundedObjects[0]);
                    }
                } else {
                    logger.warn(
                        'Brak danych w kluczu '
                            + type
                            + ' dla podanych parametrów '
                            + core.stringJson(ids)
                    );
                    return null;
                }
            } else {
                return matchedElement as Type;
            }
        } else if (this.data[type] === null) {
            logger.warn('Klucz ' + type + ' ma wartość null w danych konfiguratorów!');
            return null;
        } else {
            throw new Error('Nie ma klucza ' + type + ' w danych konfiguratorów!');
        }
    }
}
